const DESCRIPTION_PREVIEW_LINES_DESKTOP = 15;
  const DESCRIPTION_PREVIEW_LINES_MOBILE = 6;

  const MEANINGFUL_RTE_SELECTOR =
    'img, video, iframe, table, ul, ol, blockquote, hr, svg, picture, source';

  const isWhitespaceOnly = (text) => !text.replace(/[\u00a0\s\u200b\ufeff]/g, '');

  const isEmptyRteNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return isWhitespaceOnly(node.textContent);
    }

    if (!(node instanceof Element)) return false;
    if (node.matches('br')) return true;
    if (node.matches(MEANINGFUL_RTE_SELECTOR) || node.querySelector(MEANINGFUL_RTE_SELECTOR)) {
      return false;
    }

    const clone = node.cloneNode(true);
    clone.querySelectorAll('meta, style, script').forEach((el) => el.remove());
    return isWhitespaceOnly(clone.textContent);
  };

  const trimLeadingEmptyRteNodes = (container) => {
    while (container.firstChild && isEmptyRteNode(container.firstChild)) {
      container.firstChild.remove();
    }
  };

  const trimProductDescriptions = () => {
    document
      .querySelectorAll('.collapsible-content__description.rte, .product__description.rte')
      .forEach(trimLeadingEmptyRteNodes);
  };

  const getPreviewLineCount = () =>
    window.matchMedia('(max-width: 989px)').matches
      ? DESCRIPTION_PREVIEW_LINES_MOBILE
      : DESCRIPTION_PREVIEW_LINES_DESKTOP;

  const getLineHeight = (element) => {
    const style = window.getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight);
    return Number.isNaN(lineHeight) ? parseFloat(style.fontSize) * 1.5 : lineHeight;
  };

  const getPreviewHeight = (description) => {
    return Math.ceil(getLineHeight(description) * getPreviewLineCount());
  };

  const getLastVisibleTextEnd = (wrapper, description) => {
    const wrapperRect = wrapper.getBoundingClientRect();
    const lineHeight = getLineHeight(description);
    const lineBuckets = new Map();
    const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent;
      if (!text) continue;

      for (let i = 0; i < text.length; i += 1) {
        const range = document.createRange();
        range.setStart(node, i);
        range.setEnd(node, i + 1);

        for (const rect of range.getClientRects()) {
          if (rect.width === 0 && rect.height === 0) continue;
          if (rect.bottom <= wrapperRect.top + 1) continue;
          if (rect.top >= wrapperRect.bottom - 1) continue;
          if (rect.bottom > wrapperRect.bottom + 1) continue;

          const lineIndex = Math.floor((rect.top - wrapperRect.top + lineHeight * 0.15) / lineHeight);
          const existing = lineBuckets.get(lineIndex);

          if (!existing || rect.right > existing.right) {
            lineBuckets.set(lineIndex, {
              right: rect.right,
              top: rect.top,
              height: rect.height,
            });
          }
        }
      }
    }

    if (!lineBuckets.size) return null;

    const bottomLineIndex = Math.max(...lineBuckets.keys());
    return lineBuckets.get(bottomLineIndex);
  };

  const ensureEllipsis = (wrapper) => {
    let ellipsis = wrapper.querySelector('.collapsible-content__description-ellipsis');
    if (!ellipsis) {
      ellipsis = document.createElement('span');
      ellipsis.className = 'collapsible-content__description-ellipsis';
      ellipsis.setAttribute('aria-hidden', 'true');
      ellipsis.textContent = '...';
      wrapper.appendChild(ellipsis);
    }
    return ellipsis;
  };

  const updateEllipsis = (wrapper, description, container) => {
    const ellipsis = ensureEllipsis(wrapper);
    const showEllipsis =
      container.classList.contains('is-collapsible') && !container.classList.contains('is-expanded');

    if (!showEllipsis) {
      ellipsis.hidden = true;
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const textEnd = getLastVisibleTextEnd(wrapper, description);

    if (!textEnd) {
      ellipsis.hidden = true;
      return;
    }

    ellipsis.hidden = false;
    ellipsis.style.left = `${textEnd.right - wrapperRect.left}px`;
    ellipsis.style.top = `${textEnd.top - wrapperRect.top}px`;
    ellipsis.style.height = `${textEnd.height}px`;
    ellipsis.style.lineHeight = `${textEnd.height}px`;
  };

  const initProductDescriptionExpand = () => {
    const containers = document.querySelectorAll('.collapsible-content__content--product-description');

    containers.forEach((container) => {
      const wrapper = container.querySelector('.collapsible-content__description-wrapper');
      const description = container.querySelector('.collapsible-content__description');
      const toggle = container.querySelector('.collapsible-content__description-toggle');
      if (!wrapper || !description || !toggle) return;

      trimLeadingEmptyRteNodes(description);

      const applyCollapsed = () => {
        wrapper.style.overflow = 'hidden';
        wrapper.style.height = `${getPreviewHeight(description)}px`;
        requestAnimationFrame(() => updateEllipsis(wrapper, description, container));
      };

      const setup = (allowNoCollapse = false) => {
        const previewHeight = getPreviewHeight(description);
        const needsCollapse = description.scrollHeight > previewHeight + 1;
        const wasExpanded = container.classList.contains('is-expanded');

        if (!needsCollapse && allowNoCollapse) {
          container.classList.add('is-no-collapse');
          container.classList.remove('is-collapsible', 'is-expanded');
          wrapper.style.height = '';
          wrapper.style.overflow = '';
          wrapper.style.transition = '';
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          updateEllipsis(wrapper, description, container);
          container.classList.add('is-ready');
          return;
        }

        container.classList.remove('is-no-collapse');
        container.classList.add('is-collapsible');
        toggle.hidden = false;

        if (wasExpanded) {
          container.classList.add('is-expanded');
          wrapper.style.overflow = '';
          wrapper.style.height = 'auto';
          wrapper.style.transition = '';
          toggle.setAttribute('aria-expanded', 'true');
          updateEllipsis(wrapper, description, container);
        } else {
          container.classList.remove('is-expanded');
          toggle.setAttribute('aria-expanded', 'false');
          applyCollapsed();
        }

        container.classList.add('is-ready');
      };

      const scheduleSetup = () => {
        setup(false);

        const imagePromises = Array.from(description.querySelectorAll('img')).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => img.addEventListener('load', resolve, { once: true }));
        });

        const fontPromise = document.fonts?.ready ?? Promise.resolve();

        Promise.all([fontPromise, ...imagePromises]).then(() => setup(true));
      };

      if (!container.dataset.mediaBound) {
        container.dataset.mediaBound = 'true';
      }

      if (!toggle.dataset.bound) {
        toggle.dataset.bound = 'true';
        toggle.addEventListener('click', () => {
          if (!container.classList.contains('is-collapsible')) return;

          const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
          const durationMs = prefersReducedMotion ? 0 : 350;
          const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
          const isExpanded = container.classList.contains('is-expanded');

          wrapper.style.overflow = 'hidden';

          const finish = () => {
            wrapper.style.transition = '';
            if (container.classList.contains('is-expanded')) {
              wrapper.style.height = 'auto';
              updateEllipsis(wrapper, description, container);
            } else {
              wrapper.style.height = `${getPreviewHeight(description)}px`;
              requestAnimationFrame(() => updateEllipsis(wrapper, description, container));
            }
          };

          if (isExpanded) {
            const startHeight = wrapper.offsetHeight;
            wrapper.style.height = `${startHeight}px`;
            container.classList.remove('is-expanded');
            toggle.setAttribute('aria-expanded', 'false');
            void wrapper.offsetHeight;
            const endHeight = getPreviewHeight(description);
            wrapper.style.transition = `height ${durationMs}ms ${easing}`;
            wrapper.style.height = `${endHeight}px`;
          } else {
            const startHeight = wrapper.offsetHeight;
            wrapper.style.height = `${startHeight}px`;
            container.classList.add('is-expanded');
            toggle.setAttribute('aria-expanded', 'true');
            void wrapper.offsetHeight;
            const endHeight = description.scrollHeight;
            wrapper.style.transition = `height ${durationMs}ms ${easing}`;
            wrapper.style.height = `${endHeight}px`;
          }

          if (durationMs === 0) {
            finish();
            return;
          }

          const onEnd = (event) => {
            if (event.target !== wrapper || event.propertyName !== 'height') return;
            wrapper.removeEventListener('transitionend', onEnd);
            finish();
          };

          wrapper.addEventListener('transitionend', onEnd);
        });
      }

      scheduleSetup();

      if (!container.dataset.resizeBound) {
        container.dataset.resizeBound = 'true';
        window.addEventListener('resize', () => setup(true));
      }
    });
  };

trimProductDescriptions();
initProductDescriptionExpand();

document.addEventListener('DOMContentLoaded', () => {
  // Smooth open/close animation for native <details> accordions.
  // CSS-only animations on <details> are unreliable because closed content
  // is not rendered (so there is nothing to animate on collapse).
  const detailsList = document.querySelectorAll('.collapsible-content .accordion details');

  detailsList.forEach((details) => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('.accordion__content');
    const inner = details.querySelector('.accordion__content-inner');
    if (!summary || !content) return;
    if (!inner) return;

    // Ensure a stable baseline (avoid scrollbar flash during height animation).
    content.style.overflow = 'hidden';

    summary.addEventListener('click', (e) => {
      e.preventDefault();

      // Prevent double clicks mid-animation.
      if (details.dataset.animating === 'true') return;
      details.dataset.animating = 'true';

      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const durationMs = prefersReducedMotion ? 0 : 350;
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      const finish = () => {
        content.style.height = '';
        content.style.transition = '';
        // Keep overflow hidden to prevent scrollbar flash caused by `overflow-x: auto`
        // from component styles during/after transitions.
        content.style.overflow = 'hidden';
        details.dataset.animating = 'false';
        content.removeEventListener('transitionend', onEnd);
      };

      const onEnd = (evt) => {
        if (evt.target !== content || evt.propertyName !== 'height') return;
        // If we were closing, remove open only after the animation.
        if (details.dataset.closing === 'true') {
          details.removeAttribute('open');
          details.dataset.closing = 'false';
        }
        finish();
      };

      content.addEventListener('transitionend', onEnd);

      // OPEN
      if (!details.hasAttribute('open')) {
        details.setAttribute('open', '');

        // Start from 0 then grow to inner height (stable, includes padding inside inner).
        content.style.height = '0px';

        // Force reflow.
        // eslint-disable-next-line no-unused-expressions
        content.offsetHeight;

        content.style.transition = `height ${durationMs}ms ${easing}`;
        content.style.height = `${inner.scrollHeight}px`;

        if (durationMs === 0) {
          // Jump to end state.
          finish();
          details.dataset.animating = 'false';
          content.removeEventListener('transitionend', onEnd);
        }

        return;
      }

      // CLOSE
      details.dataset.closing = 'true';
      // Set current height explicitly then animate to 0.
      content.style.height = `${content.offsetHeight}px`;
      // Force reflow.
      // eslint-disable-next-line no-unused-expressions
      content.offsetHeight;
      content.style.transition = `height ${durationMs}ms ${easing}`;
      content.style.height = '0px';

      if (durationMs === 0) {
        details.removeAttribute('open');
        details.dataset.closing = 'false';
        finish();
      }
    });
  });
});